<template lang="pug">
  #root
    div#login(v-if="!user")
      .hero
        .hero-head
        .hero-body
          .container
            img.brand(src="/img/cp.png")
            h1.title TIME KIOSK
            form
              .field
                input.input.is-large.is-fullwidth(v-model="pin", placeholder="Enter your PIN")
              div
                button(type="button").button.is-primary.is-large.is-fullwidth(@click="login") Signin
        .hero-foot
    div(v-else-if = "!company") 
      .card
        .card-content.warning
          .hero.is-vcentered
            .hero-head
            .hero-body
              .container
                .title(style="color:black", v-html="no_company_heading")
                p(v-html="no_company_message")
    div(v-else-if = "!task") 
      .card
        .card-content.warning
          .hero.is-vcentered
            .hero-head
            .hero-body
              .container
                .title(style="color:black") {{no_task_heading}}
                p {{no_task_message}}
    
    div(v-else)
      nav
        TaskDisplay(:task="task")
        CompanyDisplay(:company="company")
        CrewDisplay(:crew="crew")
      #crew(v-if="crew").is-hidden-tablet
        .card(v-for='p of crew.members')
          .card-content(:class="member_state(p)")
            .columns.is-mobile
              .column.is-7
                CrewMember(:p="p")
              .column
                TimerControl(:p="p")
      div(v-if="crew").is-hidden-mobile.is-desktop(style="min-height:70vh;text-align:left;background-color:white")
        .columns.is-marginless.is-multiline(style="flex-wrap: wrap")
          .column(v-for='p of crew.members', style="max-width:32vw;min-width:32vw").is-paddingless
            .card
              .card-content(:class="member_state(p)")
                CrewMember(:p="p", style="padding-bottom:2rem;")
                TimerControl(:p="p")
    footer.footer(v-if="user")
      CompanySwitcher(v-if="companies && companies.length > 1")
      ClearPin
      Language
</template>

<script>
import { mapState } from "vuex";
import { mapGetters } from "vuex";
import TimerControl from "@/components/TimerControl";
import CrewMember from "@/components/CrewMember";
import TaskDisplay from "@/components/TaskDisplay";
import CompanyDisplay from "@/components/CompanyDisplay";
import CrewDisplay from "@/components/CrewDisplay";
import ClearPin from "@/components/ClearPin";
import Language from "@/components/Language";
import CompanySwitcher from "@/components/CompanySwitcher";

import LogService from "@/api/log";
import PinService from "@/api/pin";
import moment from "moment";

export default {
  name: "kiosk",
  data: function() {
    return {
      pin: null,
      offline: false
    };
  },
  components: {
    TimerControl,
    CrewMember,
    TaskDisplay,
    CompanyDisplay,
    CrewDisplay,
    ClearPin,
    Language,
    CompanySwitcher
  },
  computed: {
    ...mapState([
      "user",
      "crew",
      "task",
      "company",
      "timer_map",
      "language",
      "companies"
    ]),
    ...mapGetters([]),
    no_company_heading() {
      if (this.language == "en") return "No Access";
      else return "Sin acceso";
    },
    no_company_message() {
      if (this.language == "en")
        return "You must be a <b> foreman and crew leader</b> to use the kiosk app.";
      else
        return "Debes ser un líder de la tripulación para acceder al quiosco.";
    },
    no_task_heading() {
      if (this.language == "en") return "No task assigned";
      else return "Ninguna tarea asignada";
    },
    no_task_message() {
      if (this.language == "en")
        return "There is no task assigned to this crew, contact dispatch.";
      else
        return "No hay ninguna tarea asignada a esta tripulación, contacte al despacho.";
    }
  },
  mounted: function() {
    this.$store.state.member_bus.$on("member_state_change", event => {
      this.handle_state_transition(event);
    });

    this.$store.subscribe((mutation, state) => {
      if (!state.user) {
        // Logged out, do nothing with local storage, as it shoudl have already been cleared.
        localStorage.clear();
        return;
      }

      // Store the serializable state in local storage in case page refresh
      // in offline environment.
      const serializable = {
        user: state.user,
        company: state.company,
        crew: state.crew,
        task: state.task,
        companies: state.companies,
        language: state.language
      };

      localStorage.setItem(
        `kiosk-state-${this.pin}`,
        JSON.stringify(serializable)
      );

      // Now also store the current operating state for each crew member,
      // this is critical for page reloads, even with network - so the state (and similated timer)
      // can be kept current.
      if (state.crew) {
        for (const e of state.crew.members) {
          const s = state.timer_map.get(e._id);
          if (s) {
            localStorage.setItem(
              `kiosk-member-state-${e._id}`,
              JSON.stringify(s)
            );
          }
        }
      }
    });

    const stored_pin = localStorage.getItem("kiosk-pin");
    if (stored_pin) this.pin = stored_pin;
    if (this.pin) {
      this.login();
    }
  },
  methods: {
    login: async function() {
      localStorage.setItem("kiosk-pin", this.pin);
      const stored = localStorage.getItem(`kiosk-state-${this.pin}`);
      let stored_language = "en";
      if (stored) {
        const stored_object = JSON.parse(stored);

        if (stored_object.language) {
          stored_language = stored_object.language;
        }
      }

      try {
        const response = await PinService.login(this.pin);

        response.data.language = stored_language;
        this.$store.commit("login", response.data);
      } catch (ex) {
        this.offline = true;

        // Load Vuex from local storage, as long as this is the same pin
        const stored = localStorage.getItem(`kiosk-state-${this.pin}`);
        if (stored) {
          this.$store.commit("load", JSON.parse(stored));
        }
      }
      if (stored_language) {
        this.$store.commit("language", stored_language);
      }
      // After login, offline or online, load up the member state values from local storage
      if (this.crew) {
        for (const e of this.crew.members) {
          const s = localStorage.getItem(`kiosk-member-state-${e._id}`);
          if (s) {
            this.$store.commit("restore_member_state", {
              user: e,
              state: JSON.parse(s)
            });
          }
        }
      }
    },
    member_state(p) {
      const m = this.timer_map.get(p._id);
      if (m) return m.state;
      else return "absent";
    },

    make_time_log: function(employee, start, end, isBreak) {
      const log = {
        category: isBreak !== undefined ? "break" : this.task.category,
        user: employee,
        company: this.company._id,
        start: start,
        end: end,
        state: "stopped",
        seconds: Math.round(moment(end).diff(moment(start), "seconds")),
        startTime: moment(start).format("HH:mm"),
        confirmed: true,
        loggedBy: this.user._id,
        loggedOn: new Date(),
        kiosk: true
      };
      if (isBreak === undefined) {
        switch (this.task.category) {
          case "line-item":
            log.lineItem = this.task._id;
            log.job = this.task.job_id;
            break;
          case "equipment":
            log.equipment = this.task._id;
            break;
          case "workorder":
            log.workorder = this.task._id;
            break;
        }
      }
      return log;
    },
    handle_state_transition: async function(op) {
      if (op.previous && op.previous.state === "absent") {
        // Transitioning from absent to here, nothing to do.
      } else if (op.previous && op.previous.state === "break") {
        // From a break, the worker must start working... so we construct a
        // a break time log.
        const break_start = op.previous.time;
        const break_end = op.next.time;

        await LogService.send(
          this.make_time_log(op.user, break_start, break_end, "break")
        );
      } else if (op.previous && op.previous.state === "working") {
        // From working, we must send a time log for the task.
        const work_start = op.previous.time;
        const work_end = op.next.time;
        await LogService.send(
          this.make_time_log(op.user, work_start, work_end)
        );
      }
    }
  }
};
</script>

<style lang="less">
.warning {
  position: relative;
  min-height: 95vh;
  padding-top: 200px !important;
  .card {
    display: flex;
    height: 100% !important;
  }
}
#login {
  background-color: #4d8298 !important;
  height: 100vh;
}
#crew {
  text-align: left;
}

nav {
  background-color: #333;
  margin-bottom: 0px !important;
}
.working {
  .name {
    font-weight: bold;
  }
  background-color: #dcfad9 !important;
}
.absent {
  color: grey;
}
.break {
  background-color: rgb(250, 249, 219) !important;
}

img.brand {
  width: 50%;
  margin-left: auto;
  margin-right: auto;
}
h1 {
  color: white !important;
}

.footer {
  padding: 0.5rem !important;

  bottom: 0px;
  .button {
    margin-bottom: 0.5rem;
  }
}
</style>
